import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui';
import { leadImportService } from '@/services/leadImportService';
import type {
  ImportStatus,
  LeadScoreClassification,
  BatchStatus,
  ImportBatch,
  LeadImport,
  ImportSummary,
  QualificationResult,
} from '@/types/leadImport';
import styles from './LeadImportsPage.module.css';

const PAGE_SIZE = 20;

const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  COMPLETED: 'Concluído',
  FAILED: 'Falhou',
};

const BATCH_STATUS_COLORS: Record<BatchStatus, { color: string; bgColor: string }> = {
  PENDING: { color: '#495057', bgColor: '#E9ECEF' },
  PROCESSING: { color: '#004085', bgColor: '#CCE5FF' },
  COMPLETED: { color: '#155724', bgColor: '#D4EDDA' },
  FAILED: { color: '#721C24', bgColor: '#F8D7DA' },
};

const IMPORT_STATUS_LABELS: Record<ImportStatus, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  QUALIFIED: 'Qualificado',
  CONVERTED: 'Convertido',
  DUPLICATE: 'Duplicado',
  REJECTED: 'Rejeitado',
};

const IMPORT_STATUS_COLORS: Record<ImportStatus, { color: string; bgColor: string }> = {
  PENDING: { color: '#495057', bgColor: '#E9ECEF' },
  PROCESSING: { color: '#004085', bgColor: '#CCE5FF' },
  QUALIFIED: { color: '#155724', bgColor: '#D4EDDA' },
  CONVERTED: { color: '#0C5460', bgColor: '#D1ECF1' },
  DUPLICATE: { color: '#856404', bgColor: '#FFF3CD' },
  REJECTED: { color: '#721C24', bgColor: '#F8D7DA' },
};

const CLASSIFICATION_LABELS: Record<LeadScoreClassification, string> = {
  HOT: 'Hot',
  WARM: 'Warm',
  COOL: 'Cool',
  COLD: 'Cold',
};

const CLASSIFICATION_COLORS: Record<LeadScoreClassification, { color: string; bgColor: string }> = {
  HOT: { color: '#721C24', bgColor: '#F8D7DA' },
  WARM: { color: '#856404', bgColor: '#FFF3CD' },
  COOL: { color: '#004085', bgColor: '#CCE5FF' },
  COLD: { color: '#495057', bgColor: '#E9ECEF' },
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Admin page for importing and qualifying leads from external providers.
 *
 * Features:
 * - CSV upload with provider selection
 * - Recent import batches table with bulk processing
 * - Imported leads table with filtering by status and classification
 * - Individual process and convert actions
 */
export function LeadImportsPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload state
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [uploadSummary, setUploadSummary] = useState<ImportSummary | null>(null);

  // Imports filter state
  const [importPage, setImportPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ImportStatus | ''>('');
  const [classificationFilter, setClassificationFilter] = useState<LeadScoreClassification | ''>('');

  // Qualification result state
  const [qualificationResult, setQualificationResult] = useState<QualificationResult | null>(null);

  // ── Queries ────────────────────────────────────────────────

  const { data: providers } = useQuery({
    queryKey: ['lead-providers'],
    queryFn: () => leadImportService.getProviders(),
  });

  const { data: batchesData, isLoading: isBatchesLoading } = useQuery({
    queryKey: ['import-batches'],
    queryFn: () => leadImportService.getBatches({ limit: 10 }),
  });

  const { data: importsData, isLoading: isImportsLoading } = useQuery({
    queryKey: ['lead-imports', importPage, statusFilter, classificationFilter],
    queryFn: () =>
      leadImportService.getImports({
        page: importPage,
        limit: PAGE_SIZE,
        status: statusFilter || undefined,
        classification: classificationFilter || undefined,
      }),
  });

  const batches = batchesData?.items ?? [];
  const imports = importsData?.items ?? [];
  const importsTotalPages = importsData?.totalPages ?? 0;
  const importsTotalCount = importsData?.totalCount ?? 0;

  // ── Mutations ──────────────────────────────────────────────

  const uploadMutation = useMutation({
    mutationFn: ({ file, providerId }: { file: File; providerId: string }) =>
      leadImportService.uploadCsv(file, providerId),
    onSuccess: (summary) => {
      setUploadSummary(summary);
      queryClient.invalidateQueries({ queryKey: ['import-batches'] });
      queryClient.invalidateQueries({ queryKey: ['lead-imports'] });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
  });

  const processBatchMutation = useMutation({
    mutationFn: (batchId: string) => leadImportService.processBatch(batchId),
    onSuccess: (result) => {
      setQualificationResult(result);
      queryClient.invalidateQueries({ queryKey: ['import-batches'] });
      queryClient.invalidateQueries({ queryKey: ['lead-imports'] });
    },
  });

  const processImportMutation = useMutation({
    mutationFn: (id: string) => leadImportService.processImport(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lead-imports'] }),
  });

  const convertImportMutation = useMutation({
    mutationFn: (id: string) => leadImportService.convertImport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-imports'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  // ── Handlers ───────────────────────────────────────────────

  const handleUpload = useCallback(() => {
    const file = fileInputRef.current?.files?.[0];
    if (!file || !selectedProviderId) return;
    uploadMutation.mutate({ file, providerId: selectedProviderId });
  }, [selectedProviderId, uploadMutation]);

  const handleDownloadTemplate = useCallback(async () => {
    const blob = await leadImportService.downloadTemplate();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value as ImportStatus | '');
      setImportPage(1);
    },
    [],
  );

  const handleClassificationChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setClassificationFilter(e.target.value as LeadScoreClassification | '');
      setImportPage(1);
    },
    [],
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Importação de Leads</h1>
        <p className={styles.subtitle}>
          Importe, qualifique e converta leads de provedores externos.
        </p>
      </header>

      {/* ── Upload Section ────────────────────────────────── */}
      <section className={styles.uploadSection} aria-label="Upload de CSV">
        <h2 className={styles.uploadTitle}>Upload de CSV</h2>
        <div className={styles.uploadForm}>
          <div className={styles.uploadField}>
            <label htmlFor="provider-select" className={styles.uploadLabel}>
              Provedor
            </label>
            <select
              id="provider-select"
              className={styles.uploadSelect}
              value={selectedProviderId}
              onChange={(e) => setSelectedProviderId(e.target.value)}
              aria-label="Selecionar provedor"
            >
              <option value="">Selecione um provedor</option>
              {providers?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.type})
                </option>
              ))}
            </select>
          </div>
          <div className={styles.uploadField}>
            <label htmlFor="csv-file" className={styles.uploadLabel}>
              Arquivo CSV
            </label>
            <input
              id="csv-file"
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className={styles.uploadInput}
              aria-label="Selecionar arquivo CSV"
            />
          </div>
          <Button
            variant="primary"
            onClick={handleUpload}
            isLoading={uploadMutation.isPending}
            disabled={!selectedProviderId || uploadMutation.isPending}
            leftIcon={<Upload size={16} />}
          >
            Enviar
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            leftIcon={<Download size={16} />}
          >
            Baixar template
          </Button>
        </div>

        {uploadSummary && (
          <div className={styles.uploadSummary}>
            <p className={styles.uploadSummaryTitle}>Resultado do upload</p>
            <div className={styles.uploadSummaryGrid}>
              <div className={styles.uploadSummaryStat}>
                <span className={styles.uploadSummaryValue}>{uploadSummary.totalRecords}</span>
                <span className={styles.uploadSummaryLabel}>Total</span>
              </div>
              <div className={styles.uploadSummaryStat}>
                <span className={styles.uploadSummaryValue}>{uploadSummary.parsed}</span>
                <span className={styles.uploadSummaryLabel}>Processados</span>
              </div>
              <div className={styles.uploadSummaryStat}>
                <span className={styles.uploadSummaryValue}>{uploadSummary.errors}</span>
                <span className={styles.uploadSummaryLabel}>Erros</span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ── Recent Batches ────────────────────────────────── */}
      <h2 className={styles.sectionHeader}>Lotes Recentes</h2>

      {qualificationResult && (
        <div className={styles.qualificationResult}>
          <p className={styles.uploadSummaryTitle}>Resultado da qualificação</p>
          <div className={styles.qualificationGrid}>
            <div className={styles.qualificationStat}>
              <span className={styles.qualificationValue}>{qualificationResult.totalProcessed}</span>
              <span className={styles.qualificationLabel}>Processados</span>
            </div>
            <div className={styles.qualificationStat}>
              <span className={styles.qualificationValue} style={{ color: '#721C24' }}>{qualificationResult.hot}</span>
              <span className={styles.qualificationLabel}>Hot</span>
            </div>
            <div className={styles.qualificationStat}>
              <span className={styles.qualificationValue} style={{ color: '#856404' }}>{qualificationResult.warm}</span>
              <span className={styles.qualificationLabel}>Warm</span>
            </div>
            <div className={styles.qualificationStat}>
              <span className={styles.qualificationValue} style={{ color: '#004085' }}>{qualificationResult.cool}</span>
              <span className={styles.qualificationLabel}>Cool</span>
            </div>
            <div className={styles.qualificationStat}>
              <span className={styles.qualificationValue} style={{ color: '#495057' }}>{qualificationResult.cold}</span>
              <span className={styles.qualificationLabel}>Cold</span>
            </div>
            <div className={styles.qualificationStat}>
              <span className={styles.qualificationValue}>{qualificationResult.duplicates}</span>
              <span className={styles.qualificationLabel}>Duplicados</span>
            </div>
            <div className={styles.qualificationStat}>
              <span className={styles.qualificationValue}>{qualificationResult.rejected}</span>
              <span className={styles.qualificationLabel}>Rejeitados</span>
            </div>
          </div>
        </div>
      )}

      {isBatchesLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <p>Carregando lotes...</p>
        </div>
      ) : batches.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhum lote encontrado.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Arquivo</th>
                <th>Status</th>
                <th>Total</th>
                <th>Processados</th>
                <th>Novos</th>
                <th>Duplicados</th>
                <th>Erros</th>
                <th>Data</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch: ImportBatch) => (
                <tr key={batch.id}>
                  <td>{batch.fileName ?? '-'}</td>
                  <td>
                    <span
                      className={styles.badge}
                      style={{
                        color: BATCH_STATUS_COLORS[batch.status].color,
                        backgroundColor: BATCH_STATUS_COLORS[batch.status].bgColor,
                      }}
                    >
                      {BATCH_STATUS_LABELS[batch.status]}
                    </span>
                  </td>
                  <td>{batch.totalRecords}</td>
                  <td>{batch.processedRecords}</td>
                  <td>{batch.newLeads}</td>
                  <td>{batch.duplicates}</td>
                  <td>{batch.errors}</td>
                  <td>{formatDate(batch.createdAt)}</td>
                  <td>
                    <div className={styles.actions}>
                      {(batch.status === 'PENDING' || batch.status === 'COMPLETED') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => processBatchMutation.mutate(batch.id)}
                          isLoading={processBatchMutation.isPending}
                        >
                          Processar
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Imports Table ─────────────────────────────────── */}
      <h2 className={styles.sectionHeader}>Leads Importados</h2>

      <div className={styles.filters}>
        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={handleStatusChange}
          aria-label="Filtrar por status"
        >
          <option value="">Todos os status</option>
          <option value="PENDING">Pendente</option>
          <option value="PROCESSING">Processando</option>
          <option value="QUALIFIED">Qualificado</option>
          <option value="CONVERTED">Convertido</option>
          <option value="DUPLICATE">Duplicado</option>
          <option value="REJECTED">Rejeitado</option>
        </select>
        <select
          className={styles.filterSelect}
          value={classificationFilter}
          onChange={handleClassificationChange}
          aria-label="Filtrar por classificação"
        >
          <option value="">Todas as classificações</option>
          <option value="HOT">Hot</option>
          <option value="WARM">Warm</option>
          <option value="COOL">Cool</option>
          <option value="COLD">Cold</option>
        </select>
      </div>

      {isImportsLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <p>Carregando leads importados...</p>
        </div>
      ) : imports.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhum lead importado encontrado.</p>
        </div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Telefone</th>
                  <th>Cidade/UF</th>
                  <th>Segmento</th>
                  <th>Score</th>
                  <th>Classificação</th>
                  <th>LTV</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {imports.map((item: LeadImport) => (
                  <tr key={item.id}>
                    <td>{item.businessName ?? '-'}</td>
                    <td>{item.phone ?? '-'}</td>
                    <td>
                      {item.city && item.state
                        ? `${item.city}/${item.state}`
                        : item.city ?? item.state ?? '-'}
                    </td>
                    <td>{item.healthcareSegment ?? '-'}</td>
                    <td>
                      {item.score != null ? (
                        <strong>{item.score}</strong>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {item.scoreClassification ? (
                        <span
                          className={styles.badge}
                          style={{
                            color: CLASSIFICATION_COLORS[item.scoreClassification].color,
                            backgroundColor: CLASSIFICATION_COLORS[item.scoreClassification].bgColor,
                          }}
                        >
                          {CLASSIFICATION_LABELS[item.scoreClassification]}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {item.ltvEstimated != null
                        ? formatCurrency(item.ltvEstimated)
                        : '-'}
                    </td>
                    <td>
                      <span
                        className={styles.badge}
                        style={{
                          color: IMPORT_STATUS_COLORS[item.status].color,
                          backgroundColor: IMPORT_STATUS_COLORS[item.status].bgColor,
                        }}
                      >
                        {IMPORT_STATUS_LABELS[item.status]}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {item.status === 'PENDING' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => processImportMutation.mutate(item.id)}
                            isLoading={processImportMutation.isPending}
                          >
                            Processar
                          </Button>
                        )}
                        {item.status === 'QUALIFIED' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => convertImportMutation.mutate(item.id)}
                            isLoading={convertImportMutation.isPending}
                          >
                            Converter
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {importsTotalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportPage((p) => Math.max(1, p - 1))}
                disabled={importPage <= 1}
                leftIcon={<ChevronLeft size={16} />}
              >
                Anterior
              </Button>
              <span className={styles.paginationInfo}>
                Página {importPage} de {importsTotalPages} ({importsTotalCount} registros)
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportPage((p) => Math.min(importsTotalPages, p + 1))}
                disabled={importPage >= importsTotalPages}
                leftIcon={<ChevronRight size={16} />}
              >
                Próxima
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
